/**
 * Recovery for stale dynamic-import chunks after a deploy.
 *
 * Vite fingerprints every lazy chunk (e.g. `GoogleLogin-DOnUljK7.js`). Each
 * deploy produces new hashes and removes the old files from the CDN. A tab left
 * open in the background still runs the previous build's `index-*.js`, which
 * references the now-deleted chunk URLs. When such a tab lazily imports one of
 * those chunks it 404s and Vite throws "error loading dynamically imported
 * module" (message wording varies by browser), which currently crashes the app
 * into the Sentry ErrorBoundary.
 *
 * Vite fires a cancelable `vite:preloadError` event on `window` for exactly this
 * failure. We reload the page (re-fetching `index.html` and the fresh chunk
 * hashes) and call `preventDefault()` so Vite doesn't also rethrow — the reload
 * self-heals the stale tab. A short time-window guard prevents an infinite
 * reload loop when a chunk is genuinely missing (a broken deploy): in that case
 * we let Vite throw so the error still surfaces to Sentry/the ErrorBoundary.
 */

// Don't reload more than once per window — if a chunk is still missing this soon
// after a reload it's genuinely gone, not just stale, so stop reloading.
const RELOAD_GUARD_WINDOW_MS = 10_000;

const readLastReloadAt = (guardKey: string): number => {
  try {
    return Number(sessionStorage.getItem(guardKey) ?? 0);
  } catch {
    return 0;
  }
};

const markReloaded = (guardKey: string, at: number): void => {
  try {
    sessionStorage.setItem(guardKey, String(at));
  } catch {
    // sessionStorage may be unavailable (private mode / blocked storage).
    // Without it we lose the loop-guard, but a single reload attempt is still
    // strictly better than crashing.
  }
};

/**
 * Reloads the page once to pick up fresh chunk hashes. Returns `true` if a
 * reload was triggered, `false` if the guard suppressed it (reloaded too
 * recently — treat the chunk as genuinely missing).
 */
export const reloadForStaleChunk = (guardKey: string): boolean => {
  const now = Date.now();
  if (now - readLastReloadAt(guardKey) < RELOAD_GUARD_WINDOW_MS) {
    return false;
  }
  markReloaded(guardKey, now);
  window.location.reload();
  return true;
};

/**
 * Registers the `vite:preloadError` listener. Call once at app startup with an
 * app-specific `guardKey` for the sessionStorage loop guard.
 */
export const registerStaleChunkReloadListener = (guardKey: string): void => {
  window.addEventListener("vite:preloadError", (event) => {
    // Only suppress Vite's rethrow when we actually recover via reload. If the
    // guard blocks the reload, let the error propagate so a genuinely broken
    // deploy still reaches the ErrorBoundary and Sentry.
    if (reloadForStaleChunk(guardKey)) {
      event.preventDefault();
    }
  });
};
